/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Button, Cell, FormControl, Grid, HFlow, isEmpty, Theme, useTheme } from 'bold-ui'
import {
  CheckboxField,
  DateField,
  ErrorField,
  HorarioAgendaSelectField,
  HorarioAgendaSelectFieldModel,
  LocalAtendimentoSelectField,
  LocalAtendimentoSelectFieldModel,
  LotacaoAgendaSelectField,
  LotacaoAgendaSelectFieldModel,
  TextAreaField,
} from 'components/form'
import { addMonths, parseISO } from 'date-fns'
import { useHasAgendaFixadaQuery } from 'graphql/hooks.generated'
import { AcessoCbo } from 'graphql/types.generated'
import { useServerTime } from 'hooks/useServerTime'
import { Fragment } from 'react'
import { useField } from 'react-final-form'
import { isDataAgendamentoInvalida } from 'util/atendimento/isDataAgendamentoInvalida'
import { MetaPath, MetaRoot } from 'util/metaPath'

import { TipoPreNatal } from '../../pre-natal/model-preNatal'

export interface AgendarConsultaSectionModel {
  lotacao?: LotacaoAgendaSelectFieldModel
  data?: LocalDate
  horario?: HorarioAgendaSelectFieldModel
  isForaUbs?: boolean
  localAtendimento?: LocalAtendimentoSelectFieldModel
  observacoes?: string
}

export interface AgendarConsultaSectionProps {
  name: MetaRoot<AgendarConsultaSectionModel>
  errorPath: MetaPath<AgendarConsultaSectionModel>
  isProximaConsulta?: boolean
  hasPainelLateral?: boolean
  isRegistroTardio?: boolean
  tipoPreNatal?: TipoPreNatal
  setIsFormAgendarProximaConsultaOpen?(isFormOpen: boolean): void
  onSubmitFormAgendarProximaConsulta?(): void
  cbosAcesso?: AcessoCbo[]
}

export function AgendarConsultaSection(props: AgendarConsultaSectionProps) {
  const {
    name,
    errorPath,
    isProximaConsulta,
    isRegistroTardio,
    hasPainelLateral = true,
    setIsFormAgendarProximaConsultaOpen,
    onSubmitFormAgendarProximaConsulta,
    cbosAcesso,
  } = props

  const theme = useTheme()
  const styles = createStyles(theme, isProximaConsulta)

  const { getServerTimeNow } = useServerTime()
  const serverTime = getServerTimeNow()

  const {
    input: { value: lotacao },
  } = useField(name.lotacao.absolutePath(), { subscription: { value: true } })

  const {
    input: { value: data },
  } = useField(name.data.absolutePath(), { subscription: { value: true } })

  const {
    input: { value: isForaUbs },
  } = useField(name.isForaUbs.absolutePath(), { subscription: { value: true } })

  const dataAgendamentoEhInvalida = isDataAgendamentoInvalida(data, serverTime)
  const lotacaoPreenchida = !isEmpty(lotacao)

  const handleClickCancelar = () => setIsFormAgendarProximaConsultaOpen(false)
  const handleClickSalvar = () => onSubmitFormAgendarProximaConsulta()
  const {
    data: { hasAgendaFixada },
    loading,
  } = useHasAgendaFixadaQuery()

  return (
    <div css={styles.container}>
      <Grid>
        <Cell size={12}>
          <ErrorField name={errorPath} type='alert' ignoreObjectError />
        </Cell>
        <Cell md={isRegistroTardio ? 4 : 6} lg={isRegistroTardio ? 4 : hasPainelLateral ? 6 : 4}>
          {!loading && (
            <LotacaoAgendaSelectField
              name={name.lotacao}
              label='Profissional'
              required={isProximaConsulta}
              isAgendaAd={false}
              somenteCadastrarAgendamento
              cbosAcesso={cbosAcesso}
              hasAgendaFixada={hasAgendaFixada}
            />
          )}
        </Cell>
        <Cell md={3} lg={hasPainelLateral ? 3 : 2}>
          <DateField
            name={name.data}
            label='Data do agendamento'
            required={lotacaoPreenchida}
            disabled={!lotacaoPreenchida}
            minDate={serverTime}
            maxDate={addMonths(serverTime, 6)}
          />
        </Cell>
        <Cell md={3} lg={hasPainelLateral ? 3 : 2}>
          <HorarioAgendaSelectField
            name={name.horario}
            lotacaoId={lotacao?.id}
            disabled={dataAgendamentoEhInvalida}
            required={!dataAgendamentoEhInvalida}
            labels={{ inicial: 'Horário do agendamento' }}
            dia={data ? parseISO(data) : undefined}
          />
        </Cell>
        {isRegistroTardio && (
          <Fragment>
            <Cell size={2} alignSelf='center'>
              <FormControl label={<span>&nbsp;</span>}>
                <CheckboxField label='Fora da UBS' name={name.isForaUbs} />
              </FormControl>
            </Cell>
            {isForaUbs && (
              <Cell size={4}>
                <LocalAtendimentoSelectField
                  name={name.localAtendimento}
                  required={isForaUbs}
                  label='Local de atendimento'
                  excludeUbs
                />
              </Cell>
            )}
          </Fragment>
        )}
        <Cell size={isProximaConsulta ? 9 : 12}>
          <TextAreaField
            label='Observações'
            name={name.observacoes}
            resize='vertical'
            maxLength={200}
            style={styles.observacoesField}
          />
        </Cell>
        {isProximaConsulta && (
          <Cell size={3} alignSelf='center'>
            <HFlow justifyContent='flex-end'>
              <Button size='small' kind='normal' onClick={handleClickCancelar}>
                Cancelar
              </Button>
              <Button size='small' kind='primary' onClick={handleClickSalvar}>
                Salvar
              </Button>
            </HFlow>
          </Cell>
        )}
      </Grid>
    </div>
  )
}

const createStyles = (theme: Theme, isProximaConsulta: Boolean) => ({
  container: css`
    padding: 1rem;
    ${isProximaConsulta ? `background-color: ${theme.pallete.primary.c90}` : ''};
  `,
  observacoesField: css`
    min-height: 4rem;
    width: 100%;
  `,
})
